/**
 * Animation System for Amiron Intuition
 * Smooth transitions and effects for UI elements
 */
/**
 * Easing functions for smooth animations
 */
export const Easing = {
    linear: (t) => t,
    easeInOut: (t) => {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
    easeOut: (t) => {
        return t * (2 - t);
    },
    easeIn: (t) => {
        return t * t;
    },
};
/**
 * Animation Manager
 * Handles multiple concurrent animations
 */
export class AnimationManager {
    constructor() {
        this.animations = new Map();
    }
    /**
     * Start a new animation
     */
    start(id, duration, onUpdate, easing = Easing.easeInOut, onComplete) {
        this.animations.set(id, {
            startTime: performance.now(),
            duration,
            easing,
            onUpdate,
            onComplete,
        });
    }
    /**
     * Stop an animation
     */
    stop(id) {
        this.animations.delete(id);
    }
    /**
     * Update all active animations
     * Call this in the render loop
     */
    update() {
        const now = performance.now();
        const toRemove = [];
        for (const [id, animation] of this.animations.entries()) {
            const elapsed = now - animation.startTime;
            const rawProgress = Math.min(elapsed / animation.duration, 1);
            const easedProgress = animation.easing(rawProgress);
            animation.onUpdate(easedProgress);
            if (rawProgress >= 1) {
                if (animation.onComplete) {
                    animation.onComplete();
                }
                toRemove.push(id);
            }
        }
        for (const id of toRemove) {
            this.animations.delete(id);
        }
    }
    /**
     * Check if any animations are active
     */
    hasActiveAnimations() {
        return this.animations.size > 0;
    }
    /**
     * Clear all animations
     */
    clear() {
        this.animations.clear();
    }
}
/**
 * Global animation manager instance
 */
export const globalAnimationManager = new AnimationManager();
